import { addBreadcrumb } from '@sentry/react';

import { type DialogToParentMessage, isDialogToParentMessage } from '@/api/types/authMessages';

import { markDialogClosed, recordDialogClosed } from './officeDialogManager';

/**
 * The wait-for-the-dialog half of the Office dialog auth flows, shared by the
 * Clerk login dialog and the Connect Mailbox dialog.
 *
 * Both surfaces need the same hard-won handling: unwrap whatever shape the
 * host delivered, ignore stray messages rather than dying on the first one,
 * close in the order officeDialogManager expects, and bound the wait. Keeping
 * one copy means the next host quirk gets fixed for both at once — two copies
 * is how one flow silently keeps dropping sign-ins the other learned to
 * handle.
 *
 * Deliberately never rejects. Each caller decides what an outcome means: a
 * closed dialog is a cancellation to one surface and an unanswered question to
 * the other.
 */

/** Redacts JWT-shaped substrings and truncates, so raw messages are safe to log. */
function sanitizeRawMessage(raw: unknown): string {
  let text: string;
  if (typeof raw === 'string') {
    text = raw;
  } else {
    try {
      text = `<${typeof raw}> ${JSON.stringify(raw)}`;
    } catch {
      return `<unserializable: ${typeof raw}>`;
    }
  }
  return text.replaceAll(/eyJ[\w-]+\.[\w-]+\.[\w-]+/g, '<jwt>').slice(0, 200);
}

// Bounds the unwrap loop below: object envelope → string → parsed object is 3
// steps; one spare for a double-stringified payload.
const MAX_UNWRAP_STEPS = 4;

/**
 * Extracts a DialogToParentMessage from whatever shape the dialog channel
 * delivered. `messageParent` sends a JSON string and most hosts hand it over
 * verbatim, but the web host's popup-blocked new-window fallback delivers
 * `arg.message` as an already-parsed object, sometimes wrapping the payload
 * under a nested `message` key or double-stringifying it. Unwrap those shapes
 * instead of rejecting them — rejecting kills the sign-in silently once the
 * dialog closes.
 */
export function parseDialogMessage(raw: unknown): DialogToParentMessage | undefined {
  let value: unknown = raw;
  for (let step = 0; step < MAX_UNWRAP_STEPS; step++) {
    if (isDialogToParentMessage(value)) {
      return value;
    }
    if (typeof value === 'string') {
      try {
        value = JSON.parse(value);
        continue;
      } catch {
        return undefined;
      }
    }
    if (typeof value === 'object' && value !== null && 'message' in value) {
      value = (value as { message: unknown }).message;
      continue;
    }
    return undefined;
  }
  return undefined;
}

/** How the wait ended. `closed` carries the host's event code when it gave one. */
export type DialogOutcome =
  | { code: number | undefined; ignoredMessageCount: number; type: 'closed' }
  | { ignoredMessageCount: number; message: DialogToParentMessage; type: 'message' }
  | { ignoredMessageCount: number; type: 'timeout' };

/** Nulls out the manager's handle, then closes and records — in that order. */
function closeDialog(dialog: Office.Dialog): void {
  // activeDialog first: a programmatic close fires no DialogEventReceived, so
  // without this the manager keeps a dead reference and warns about a stale
  // dialog on the next attempt.
  markDialogClosed();
  try {
    dialog.close();
  } catch {
    // Already closing — nothing to do.
  }
  recordDialogClosed();
}

/**
 * Waits for an Office dialog to deliver a recognised message, close, or run
 * out of time, closing it on the paths where the host will not.
 */
export function awaitDialogOutcome(
  dialog: Office.Dialog,
  options: { label: string; timeoutMs: number },
): Promise<DialogOutcome> {
  const { label, timeoutMs } = options;

  return new Promise<DialogOutcome>((resolve) => {
    let ignoredMessageCount = 0;

    const timeout = setTimeout(() => {
      addBreadcrumb({
        message: `[${label}] dialog timed out after ${timeoutMs}ms (ignored ${ignoredMessageCount} unrecognized message(s))`,
        type: 'warning',
      });
      closeDialog(dialog);
      resolve({ ignoredMessageCount, type: 'timeout' });
    }, timeoutMs);

    dialog.addEventHandler(Office.EventType.DialogMessageReceived, (arg) => {
      const { message: raw, origin } = arg as { message?: unknown; origin?: string };

      const message = parseDialogMessage(raw);
      if (!message) {
        // Stray or mangled message from the popup-blocked fallback. Treating
        // the first one as fatal kills the whole sign-in, so ignore it and keep
        // waiting — the timeout still bounds this.
        ignoredMessageCount++;
        addBreadcrumb({
          data: { origin, raw: sanitizeRawMessage(raw) },
          message: `[${label}] ignoring unrecognized dialog message`,
          type: 'warning',
        });
        return;
      }

      closeDialog(dialog);
      clearTimeout(timeout);

      addBreadcrumb({
        data: { ignoredMessageCount, type: message.type },
        message: `[${label}] dialog message accepted`,
      });
      resolve({ ignoredMessageCount, message, type: 'message' });
    });

    dialog.addEventHandler(Office.EventType.DialogEventReceived, (arg) => {
      clearTimeout(timeout);
      const code = (arg as { error?: number }).error;
      addBreadcrumb({
        data: { error: code, ignoredMessageCount },
        message: `[${label}] dialog closed without an accepted message`,
        type: ignoredMessageCount > 0 ? 'warning' : 'info',
      });
      resolve({ code, ignoredMessageCount, type: 'closed' });
    });
  });
}
